<template>
  <div v-bind="$attrs" :class="rootStyles">
    <JerseyNumbers v-if="isJerseyNumbers" class="product-details-dtf__jersey-numbers" />
    <Letters v-if="isLetters" class="product-details-dtf__letters" />

    <div
      v-if="firstArtwork && !firstArtwork.isSimulated && !showStepsUI"
      class="product-details-dtf__file-name"
      :class="{
        'product-details-dtf__file-name--with-error': isGenericErrorVisible,
        'product-details-dtf__file-name--full-width': showFullWidthArtwork
      }"
    >
      <UseSvg
        v-if="!!firstArtwork.metadata || isGenericErrorVisible"
        :icon="headerIcon"
        class="product-details-dtf__header-icon"
        :class="{ 'product-details-dtf__header-icon--with-error': isGenericErrorVisible }"
      />
      <span class="product-details-dtf__header-text"> {{ firstArtwork.name }} </span>
    </div>

    <div
      v-if="!outOfStock && !isMobile && ($slots.dates || (gangSheetPdp && $slots['free-shipping']))"
      class="product-details-dtf__dates-container hidden-xs"
    >
      <slot v-if="gangSheetPdp" name="free-shipping" />
      <DeliveryDate />
    </div>

    <template v-if="isGangSheetBuilder">
      <GangSheetBuilderPdpContainer :current-store="currentStore" />
      <slot name="saved-carts"></slot>
    </template>
    <template v-if="!isJerseyNumbers && !isLetters && !isGangSheetBuilder">
      <template v-if="showPromotionBanner">
        <div
          v-if="promotionWinner !== 'bogo'"
          class="product-details-dtf__promotion-banner"
          :class="'product-details-dtf__promotion-banner--winner-' + promotionWinner"
        >
          <slot name="promotion-banner" />
        </div>
        <BogoInfo
          v-else-if="promotionWinner === 'bogo'"
          :reason="bogoEligibility.reason"
          :is-guest="isGuest"
          :max-free-dimension-label="bogoMaxDimensionLabel"
        />
      </template>
      <ProColorUpsell
        v-if="showProColorUpsell && isStandardArtwork"
        :artwork="firstArtwork"
        class="product-details-dtf__pro-color-upsell"
      />
      <DesignTagCreationPanel v-if="!outOfStock && neckLabel" />
      <StepWrapper
        v-if="!outOfStock && !neckLabel"
        class="product-details-dtf__step-1"
        :has-error="showStepsUI && isGenericErrorVisible"
        :borderless="!showStepsUI"
        :step-number="1"
        header-text="Upload Your Design(s)"
      >
        <template #header-badge>
          <FreeTransfersBadge
            v-if="showFreeTransfersBadge"
            :message="freeTransfersBadgeMessage"
            class="product-details-dtf__free-transfers-badge"
          />
        </template>
        <template v-if="showCancelButton" #step-number-content>
          <button
            type="button"
            class="product-details-dtf__cancel"
            data-testid="transfers-cancel-button"
            @click="handleCancelProcess(firstArtwork)"
          >
            <UseSvg class="product-details-dtf__cancel-icon" icon="modal-close" />
            Cancel
          </button>
        </template>
        <ArtworkInfo :artwork="firstArtwork" :show-file-info="showArtworkFileInfo" :index="0" />
        <ArtworkImage
          :key="firstArtwork.id"
          :artwork="firstArtwork"
          :active-config-section="activeConfigSection"
          show-full-screen-button
          @click="showFileDialog"
        />
        <TrimInfo
          v-if="!isStandardArtwork && !firstArtwork.isFile && !firstArtwork.isSimulated"
          :id="firstArtwork.id"
          :artwork="firstArtwork"
          :background="firstArtwork.background"
          :can-remove-bg="firstArtwork.canRemoveBg"
          :remove-background="firstArtwork.removeBackground"
          :toggle-background-error="firstArtwork.toggleBackgroundError"
        />
        <ArtworkQualityConfigPanel
          v-if="shouldShowArtworkQualityConfigPanel"
          :key="firstArtwork.id"
          :artwork="firstArtwork"
          :active-config-section="activeConfigSection"
          @set-active-config-section="setActiveConfigSection"
        />
        <ArtworkQualityConfigPanelAbAiMonetization
          v-if="shouldShowArtworkQualityConfigPanelAbAiMonetization"
          :index="0"
          :artwork="firstArtwork"
          :reorder-enabled="reorderEnabled"
          :active-config-section="activeConfigSection"
          :paid-expert-edit-addon-enabled="paidExpertEditAddonEnabled"
          @set-active-config-section="setActiveConfigSection"
        />
        <PuffColorSelector v-if="isPuff && !showStepsUI" label="Select Puff Color" />
        <template v-if="sticker && artworkUploaded && experiments.stickers_style_options">
          <StickersTools :artwork="firstArtwork" class="product-details-dtf__section" />
        </template>
        <template v-if="!isStandardArtwork">
          <RedrawNotes v-if="showRedrawNote" :enhancement-note="firstArtwork.enhancementNote" />
          <ProColorUpsell
            v-if="showProColorUpsell"
            :artwork="firstArtwork"
            class="product-details-dtf__section"
          />
        </template>
        <ArtworkCTA v-if="showArtworkCTA" :artwork-url="firstArtwork.url" />
        <ArtworkError
          v-if="withError && !isMultiSizeError"
          :artwork="firstArtwork"
          :current-store="currentStore"
        />
        <ArtworkNotification v-if="!withError && warningMessage">
          <b>{{ warningMessage }}</b>
        </ArtworkNotification>
      </StepWrapper>
      <PuffColorSelector v-if="isPuff && showStepsUI" label="Select Puff Color" />
      <template v-if="!showModal && !neckLabel">
        <MultiSizeStep
          v-if="showMultiSize"
          :artwork="firstArtwork"
          :disabled="disableSizeChange"
          :readonly="printAsIsReadOnly"
          :with-pre-cut="withPreCut"
          :show-multi-upload-box="showMultiUploadBox"
          :is-artwork-uploaded="artworkUploaded"
          :show-free-transfers-badge="showFreeTransfersBadge"
          @show-file-dialog="showFileDialog"
        />
        <ArtworkDimensions
          v-else-if="!isStickerSample && !showMultiSize"
          :artwork="firstArtwork"
          :class="{
            'product-details-dtf__dimensions--near-quantity':
              !showStepsUI && printAsIs && modalArtworks.length <= 1
          }"
          :disabled="disableSizeChange"
          :width-default="defaultWidthValue"
          :readonly="printAsIsReadOnly"
          :current-store="currentStore"
        />
      </template>
      <DtfSpanglesActions v-if="showSpanglesActions" :is-artwork-invalid="withError" />
      <DtfSpangleVersions v-if="showSpangleVersions && !isSpangleReordering" />
      <DtfSpanglesColorSelector v-if="showSpangleVersions || isSpangleReordering" />
    </template>
    <template v-if="isStickerSample">
      <input
        class="artwork-quantity__input js-quantity-form-input"
        type="hidden"
        :data-variant-id="variantId"
        :value="10"
      />
    </template>
    <ArtworkQuantity
      v-else-if="!showMultiSize && !isGangSheetBuilder && !neckLabel"
      :artwork="firstArtwork"
      show-free-shipping
      :disabled="outOfStock"
    />
    <template v-if="!neckLabel">
      <PreCut v-if="withPreCut && !showMultiSize && !neckLabel" />
      <NotifyOnRestock v-if="outOfStock" />
      <DropZone v-if="!outOfStock && !neckLabel" />
    </template>

    <input
      id="product-details-dtf-file-input"
      ref="file"
      type="file"
      data-testid="transfers-file-input"
      :accept="supportedFileTypes"
      class="product-details-dtf__file hidden"
      :multiple="(!isSpangle || spanglesMaxImageCount > 1) && !isPuff && !isStickerSample"
      @change="handleFileChange"
    />

    <MultiArtworkModal
      v-if="showModal"
      :key="firstArtwork.id"
      :move-bellow="
        !!fileToRemove ||
        showDiscardModal ||
        showFilesError ||
        showDtfRetentionModal ||
        showPinningModal
      "
      :current-store="currentStore"
      :active-config-section="activeConfigSection"
      @upload="showFileDialog"
    >
      <template #saved-carts>
        <slot name="saved-carts-modal"></slot>
      </template>
      <template #free-shipping>
        <slot name="free-shipping"></slot>
      </template>
    </MultiArtworkModal>

    <PinningSessionModal
      v-if="showPinningModal && pinningArtwork && pinningModalSessionId"
      :artwork-index="pinningArtworkIndex"
      :artwork-image-url="pinningArtworkImageUrl"
      :artwork-id="pinningArtworkId"
      :session-id="pinningModalSessionId"
    />

    <DtfConfirmationModal
      v-if="pendingPinningInvalidation"
      @submit="confirmPinningInvalidation"
      @close="cancelPinningInvalidation"
    >
      <template #header>Remove Design Request?</template>
      <template #text>
        This change will remove your design request, are you sure you want to proceed?
      </template>
      <template #button>Yes, Remove</template>
      <template #cancel>Keep Request</template>
    </DtfConfirmationModal>

    <ArtworkHistoryModal v-if="showArtworkHistoryModal" :reorder-enabled="reorderEnabled" />

    <!-- Mobile DeliveryDate -->
    <div v-if="!outOfStock" class="product-details-dtf__dates-container visible-xs">
      <slot v-if="gangSheetPdp" name="free-shipping" />
      <DeliveryDate />
    </div>

    <StickerSampleBanner v-if="isStickerSample" />

    <div v-if="showMultiUploadBox && !showMultiSize" class="product-details-dtf__upload">
      <UploadBox @upload="showFileDialog" />
    </div>

    <template v-if="!isGangSheetBuilder">
      <StepWrapper v-if="showMultiSize" :step-number="3" header-text="Review and Add to Cart">
        <MultiSizeTotal />
        <div class="js-product-details-delivery-date-pdp-container hidden">
          <DeliveryDate hide-address-line compact is-same-day />
          <span class="product-details-dtf__or">Or</span>
        </div>
        <slot v-if="showShippingProgress" name="free-shipping"></slot>
        <div v-if="showStoreCredit" class="product-details-dtf__store-credit">
          <TransfersStoreCredit :store-credit-info="storeCreditInfo" />
        </div>
        <slot name="saved-carts"></slot>
        <DtfAddToCart v-if="!showModal" />
        <slot name="favorite"></slot>
      </StepWrapper>
      <template v-else>
        <div>
          <TransfersStoreCredit v-if="showStoreCredit" :store-credit-info="storeCreditInfo" />
          <slot name="saved-carts"></slot>
          <DtfAddToCart v-if="!showModal && !neckLabel" />
        </div>
      </template>
    </template>

    <DtfConfirmationModal v-if="fileToRemove" @close="resetRemovalFile" @submit="onRemoveArtwork">
      <template #header>Remove File</template>
      <template #text>This file will be removed. Do you confirm?</template>
      <template #button>Remove File</template>
    </DtfConfirmationModal>

    <DtfConfirmationModal v-if="showDiscardModal" @close="hideDiscardModal" @submit="onDiscard">
      <template #header>Discard Changes</template>
      <template #text>
        All the changes and artworks will be discarded.<br />Are you sure you want to proceed?
      </template>
      <template #button>Discard Changes</template>
    </DtfConfirmationModal>

    <DtfConfirmationModal v-if="showFilesError" @close="resetFileErrors">
      <template #header>Warning</template>
      <template #text> You can only upload up to 10 files simultaneously. </template>
      <template #cancel>OK</template>
    </DtfConfirmationModal>

    <GangSheetInfoModal v-if="showGangSheetInfoModal" :current-store="currentStore" />
  </div>
  <DtfTransferUserRetentionModal
    v-model:is-open="showDtfRetentionModal"
    :is-multi-artwork-modal-open="showModal"
    :is-pin-based-redraw-enabled="isPinBasedRedrawEnabled"
    :trigger-point="triggerPoint"
  />
</template>

<script>
// Global imports
import { mapActions, mapState } from "pinia";
import { nextTick, ref, toRef } from "vue";
import { useI18n } from "vue-i18n";

// Absolute paths
import { acceptedFileTypes } from "common/file-type-helper";
import { readCookie } from "common/utils/cookie";
import initDeliveryDateSimple from "components/jiffy/product-details-delivery-date-simple/product-details-delivery-date-simple";
import initProductSavedCartsSelector from "components/jiffy/product-saved-carts-selector/product-saved-carts-selector";
import BogoInfo from "components/jiffy/vue-product-details-dtf/BogoInfo.vue";
import { useUserBackButtonClickDetector } from "components/jiffy/vue-product-details-dtf/common/composables/useUserBackButtonClickDetector";
import { useUserInactivityDetector } from "components/jiffy/vue-product-details-dtf/common/composables/useUserInactivityDetector";
import JerseyNumbers from "components/jiffy/vue-product-details-dtf/JerseyNumbers/JerseyNumbers.vue";
import Letters from "components/jiffy/vue-product-details-dtf/Letters/Letters.vue";
import ProColorUpsell from "components/jiffy/vue-product-details-dtf/ProColorUpsell.vue";
import PuffColorSelector from "components/jiffy/vue-product-details-dtf/PuffColorSelector.vue";
import StickersTools from "components/jiffy/vue-product-details-dtf/Stickers/StickersTools/StickersTools.vue";
import {
  isArtworkWithError,
  toggleGlobalDtfError
} from "components/jiffy/vue-product-details-dtf/utils";
import { api } from "jiffy-api";
import { EventBus, Events } from "jiffy-store";

// Relative paths
import UseSvg from "common/vue/components/UseSvg.vue";
import DtfTransferUserRetentionModal from "components/jiffy/vue-product-details-dtf/common/DtfTransferUserRetentionModal.vue";
import {
  isDtfUserRetentionModalShown,
  setDtfUserRetentionModalShown
} from "components/jiffy/vue-product-details-dtf/common/dtfUserRetentionHelper";
import ArtworkCTA from "./ArtworkCTA.vue";
import ArtworkDimensions from "./ArtworkDimensions.vue";
import ArtworkError from "./ArtworkError.vue";
import ArtworkHistoryModal from "./ArtworkHistoryModal.vue";
import ArtworkImage from "./ArtworkImage.vue";
import ArtworkInfo from "./ArtworkInfo.vue";
import MultiSizeStep from "./ArtworkMultiSize/MultiSizeStep.vue";
import MultiSizeTotal from "./ArtworkMultiSize/MultiSizeTotal.vue";
import ArtworkNotification from "./ArtworkNotification.vue";
import ArtworkQualityConfigPanel from "./ArtworkQualityConfigPanel.vue";
import ArtworkQualityConfigPanelAbAiMonetization from "./ArtworkQualityConfigPanel/ArtworkQualityConfigPanel.vue";
import ArtworkQuantity from "./ArtworkQuantity.vue";
import {
  DTF001_CATALOG_NUMBER,
  INACTIVITY_INTERVAL,
  INACTIVITY_MAX_CALL_COUNT,
  MODAL_TRIGGER_POINTS,
  PDP_TYPE_GANG_SHEETS
} from "./common/constants";
import FreeTransfersBadge from "./common/FreeTransfersBadge.vue";
import StepWrapper from "./common/StepWrapper.vue";
import TransfersStoreCredit from "./common/TransfersStoreCredit.vue";
import DeliveryDate from "./DeliveryDate.vue";
import DesignTagCreationPanel from "./DesignTagCreationPanel.vue";
import DropZone from "./DropZone.vue";
import DtfAddToCart from "./DtfAddToCart.vue";
import DtfConfirmationModal from "./DtfConfirmationModal.vue";
import DtfSpanglesActions from "./DtfSpanglesActions.vue";
import DtfSpanglesColorSelector from "./DtfSpanglesColorSelector.vue";
import DtfSpangleVersions from "./DtfSpangleVersions.vue";
import GangSheetBuilderPdpContainer from "./GangSheetBuilderPdp/GangSheetBuilderPdpContainer.vue";
import GangSheetInfoModal from "./GangSheetInfoModal.vue";
import MultiArtworkModal from "./MultiArtworkModal.vue";
import NotifyOnRestock from "./NotifyOnRestock.vue";
import PinningSessionModal from "./PinningSession/PinningSessionModal.vue";
import PreCut from "./PreCut.vue";
import RedrawNotes from "./RedrawNotes.vue";
import { SPANGLE_MODES, useSpanglesStore } from "./spangles-module";
import StickerSampleBanner from "./StickerSampleBanner.vue";
import { useDtfStore } from "./store";
import TrimInfo from "./TrimInfo.vue";
import UploadBox from "./UploadBox.vue";

const createInactivityDetector = handleOpenRetentionModal =>
  useUserInactivityDetector({
    interval: INACTIVITY_INTERVAL,
    maxCallCount: INACTIVITY_MAX_CALL_COUNT,
    onInactive: () => handleOpenRetentionModal(MODAL_TRIGGER_POINTS.INACTIVITY)
  });

const createBackButtonDetector = handleOpenRetentionModal =>
  useUserBackButtonClickDetector({
    onBackButtonClick: () => handleOpenRetentionModal(MODAL_TRIGGER_POINTS.BACK_BUTTON)
  });

const hasVisibleArtworks = artworks =>
  artworks.length > 0 &&
  artworks.some(
    artwork =>
      artwork &&
      !artwork.isSimulated &&
      !artwork.isFile &&
      !artwork.uploadError &&
      !artwork.isLoading &&
      artwork.id &&
      typeof parseInt(artwork.id, 10) === "number" &&
      !Number.isNaN(parseInt(artwork.id, 10)) &&
      artwork.enhancementCompleted === true
  );

const hasArtworksWithPinningSession = artworks =>
  artworks.length > 0 && artworks.some(artwork => Boolean(artwork?.pinningSessionId));

export default {
  name: "ProductDetailsDtf",
  components: {
    JerseyNumbers,
    DtfTransferUserRetentionModal,
    UploadBox,
    ArtworkCTA,
    ArtworkImage,
    ArtworkDimensions,
    ArtworkQuantity,
    ArtworkQualityConfigPanel,
    ArtworkQualityConfigPanelAbAiMonetization,
    MultiArtworkModal,
    DtfAddToCart,
    ArtworkInfo,
    DtfConfirmationModal,
    TrimInfo,
    GangSheetInfoModal,
    PreCut,
    StickerSampleBanner,
    ArtworkError,
    ArtworkNotification,
    UseSvg,
    ArtworkHistoryModal,
    NotifyOnRestock,
    DropZone,
    DtfSpanglesActions,
    DtfSpangleVersions,
    DtfSpanglesColorSelector,
    PuffColorSelector,
    BogoInfo,
    ProColorUpsell,
    Letters,
    StickersTools,
    RedrawNotes,
    MultiSizeStep,
    StepWrapper,
    FreeTransfersBadge,
    MultiSizeTotal,
    TransfersStoreCredit,
    GangSheetBuilderPdpContainer,
    DeliveryDate,
    DesignTagCreationPanel,
    PinningSessionModal
  },
  inheritAttrs: false,
  props: {
    locale: {
      type: String,
      default: "en-US"
    },
    currentStore: {
      type: String,
      default: null
    }
  },
  setup(props) {
    const { locale, t } = useI18n({ useScope: "global" });
    const pdpLocale = toRef(props, "locale");
    const pdpStore = useDtfStore();
    const triggerPoint = ref(MODAL_TRIGGER_POINTS.NONE);
    const showDtfRetentionModal = ref(false);

    pdpStore.setLocale(pdpLocale.value);
    locale.value = pdpLocale.value;

    const closeRetentionModal = () => {
      showDtfRetentionModal.value = false;
    };

    const handleOpenRetentionModal = triggerVal => {
      if (
        !isDtfUserRetentionModalShown() &&
        (!pdpStore.isPinBasedRedrawEnabled ||
          (!pdpStore.showPinningModal && !hasArtworksWithPinningSession(pdpStore.artworks))) &&
        hasVisibleArtworks(pdpStore.artworks)
      ) {
        showDtfRetentionModal.value = true;
        triggerPoint.value = triggerVal;
        setDtfUserRetentionModalShown();
      }
    };

    const { triggerUserInactivityMonitoring, stopMonitoring, resetTimer } =
      createInactivityDetector(handleOpenRetentionModal);

    const { triggerUserBackButtonClickMonitoring } =
      createBackButtonDetector(handleOpenRetentionModal);

    return {
      t,
      triggerPoint,
      showDtfRetentionModal,
      closeRetentionModal,
      triggerUserInactivityMonitoring,
      stopMonitoring,
      resetTimer,
      triggerUserBackButtonClickMonitoring
    };
  },
  data() {
    return {
      sizeMessageMaxSize: 10,
      showComparisonModal: false,
      activeConfigSection: "jiffy-print-perfect", // jiffy-print-perfect | print-as-uploaded
      hasNoPrintTransfersHistory: null,
      cartHasBlanks: false
    };
  },
  computed: {
    ...mapState(useDtfStore, [
      "artworks",
      "files",
      "fileToRemove",
      "showDiscardModal",
      "showFilesError",
      "replaceArtwork",
      "modalArtworks",
      "outOfStock",
      "pdpType",
      "printAsIs",
      "showCTA",
      "showModal",
      "sticker",
      "withPreCut",
      "showArtworkHistoryModal",
      "isMobile",
      "catalogNumber",
      "isSticker",
      "isDieCutSticker",
      "isStickerSample",
      "isSpangle",
      "isPuff",
      "isLetters",
      "isFirstArtworkMinQuantityInvalid",
      "variantId",
      "experiments",
      "isJerseyNumbers",
      "isGuest",
      "showStepsUI",
      "showRedrawUI",
      "showMultiSize",
      "isStandardArtwork",
      "enableAbAiMonetization",
      "simulation",
      "storeCreditInfo",
      "isGangSheetBuilder",
      "showModal",
      "reorderEnabled",
      "paidExpertEditAddonEnabled",
      "neckLabel",
      "blankItemsCount",
      "transfersInCartCount",
      "isBogoApplicable",
      "isNewToTransfers",
      "transferPdpInactivityDetection",
      "transferPdpBackButtonDetection",
      "isPinBasedRedrawEnabled",
      "bogoEligibility",
      "bogoMaxDimensionLabel",
      "promotionResolution",
      "showPinningModal",
      "pinningArtworkIndex",
      "pinningModalSessionId",
      "pendingPinningInvalidation",
      "lastConfirmedConfigSection"
    ]),
    ...mapState(useSpanglesStore, [
      "selectedAlgorithm",
      "spangleVariations",
      "spangleMode",
      "lastSpangledDimensions",
      "spangleSizeEdited",
      "spanglesLoading",
      "spanglesMaxImageCount",
      "invalidSpangleSelected",
      "allAPIsCompleted",
      "allAPIsFailed",
      "isSpangleReordering",
      "originalArtworkUrl"
    ]),
    pinningArtwork() {
      if (this.pinningArtworkIndex === null) return null;
      return this.artworks[this.pinningArtworkIndex] ?? null;
    },
    pinningArtworkImageUrl() {
      if (!this.pinningArtwork) return "";
      const artwork = this.pinningArtwork;

      if (artwork.manualEditUrl) {
        return artwork.manualEditUrl;
      }

      // Print As Is: show original image
      if (artwork.enhancementTier === "print_original") {
        return artwork.originalImageUrl || artwork.url || "";
      }

      if (!this.isStandardArtwork) {
        return artwork.url || artwork.originalImageUrl || "";
      }

      if (artwork.removeBackground && artwork.canRemoveBg) {
        return (
          artwork.enhancedNoBgUrl ||
          artwork.sliderPreviewUrl ||
          artwork.enhancedWithBgUrl ||
          artwork.originalImageUrl ||
          artwork.url ||
          ""
        );
      }
      return artwork.enhancedWithBgUrl || artwork.originalImageUrl || artwork.url || "";
    },
    pinningArtworkId() {
      if (!this.pinningArtwork) return "";
      return String(this.pinningArtwork.id || "");
    },
    shouldShowArtworkQualityConfigPanel() {
      if (!this.hasArtwork) {
        return false;
      }

      return (
        this.isStandardArtwork &&
        !this.printAsIs &&
        !this.firstArtwork.isFile &&
        !this.firstArtwork.isSimulated &&
        !this.enableAbAiMonetization
      );
    },
    shouldShowArtworkQualityConfigPanelAbAiMonetization() {
      if ((this.withError && !this.isMultiSizeError) || !this.hasArtwork) {
        return false;
      }

      return (
        this.isStandardArtwork &&
        !this.printAsIs &&
        !this.firstArtwork.isFile &&
        !this.firstArtwork.isSimulated &&
        this.enableAbAiMonetization
      );
    },
    displayEnhancementFeedback() {
      return (
        this.enableAbAiMonetization &&
        this.isStandardArtwork &&
        !this.firstArtwork.isSimulated &&
        this.firstArtwork.id &&
        this.firstArtwork.enhancementCompleted &&
        this.firstArtwork.enhancementTier === "full_enhancements"
      );
    },
    sampleItemPrice() {
      return Math.round(this.simulation.pricePerInch * 100) / 100;
    },
    firstArtwork() {
      return this.artworks[0];
    },
    isArtworkLoading() {
      return this.firstArtwork?.isLoading;
    },
    artworkUploaded() {
      const artworkId = parseInt(this.firstArtwork?.id, 10);
      if (Number.isNaN(artworkId)) {
        return false;
      }

      return typeof artworkId === "number" && !this.isArtworkLoading;
    },
    warningMessage() {
      return this.firstArtwork?.metadata?.warnings?.human;
    },
    defaultWidthValue() {
      if (!this.printAsIs) return "";

      return "22";
    },
    printAsIsReadOnly() {
      return this.printAsIs && !!this.firstArtwork?.width && !!this.firstArtwork?.height;
    },
    showSpanglesActions() {
      return this.isSpangle && this.artworkUploaded && !this.isArtworkLoading;
    },
    showSpangleVersions() {
      return this.showSpanglesActions && this.allAPIsCompleted;
    },
    showMultiUploadBox() {
      return (
        !this.files.length &&
        !this.firstArtwork.isSimulated &&
        !this.isSpangle &&
        !this.isPuff &&
        !this.isStickerSample
      );
    },
    showGangSheetInfoModal() {
      return (
        !this.isGuest && this.gangSheetPdp && readCookie("gang-sheet-info-modal-shown") !== "true"
      );
    },
    gangSheetPdp() {
      return this.pdpType === PDP_TYPE_GANG_SHEETS;
    },
    supportedFileTypes() {
      return acceptedFileTypes(this.printAsIs);
    },
    showArtworkCTA() {
      return this.showCTA && this.firstArtwork?.url;
    },
    isMultiSizeError() {
      return this.firstArtwork?.isMultiSizeError;
    },
    hasArtwork() {
      return this.firstArtwork && !this.firstArtwork.isSimulated && !this.firstArtwork.uploadError;
    },
    withError() {
      return isArtworkWithError(this.firstArtwork);
    },
    isGenericErrorVisible() {
      if (!this.firstArtwork || this.firstArtwork.isSimulated) {
        return false;
      }
      return this.withError || this.isFirstArtworkMinQuantityInvalid;
    },
    headerIcon() {
      return this.isGenericErrorVisible ? "strong-info" : "check-circle-bold";
    },
    fileSize() {
      return Math.floor((this.firstArtwork?.file?.size || 0) * 0.000001 || 0);
    },
    remainingTime() {
      const spangleTime = this.isSpangle ? 1 : 0;

      if (Math.ceil(this.fileSize / 10) < 1) {
        return 1 + spangleTime;
      }
      return Math.min(Math.ceil(this.fileSize / 10), this.sizeMessageMaxSize) + spangleTime;
    },
    disableSizeChange() {
      if (this.isStandardArtwork) {
        return this.outOfStock || this.isArtworkLoading || !this.firstArtwork.enhancementCompleted;
      }
      if (this.isSpangle) {
        if (this.allAPIsFailed) {
          return this.isArtworkLoading;
        }
        return (
          this.outOfStock ||
          this.spangleSizeEdited ||
          this.spanglesLoading ||
          this.isArtworkLoading ||
          this.invalidSpangleSelected ||
          !this.allAPIsCompleted
        );
      }
      return this.outOfStock || this.isArtworkLoading;
    },
    showArtworkFileInfo() {
      return !this.isSpangle;
    },
    isDtfUsaStore() {
      return this.catalogNumber === DTF001_CATALOG_NUMBER && this.currentStore === "usa";
    },
    promotionWinner() {
      return this.promotionResolution?.winner ?? "none";
    },
    showPromotionBanner() {
      return this.promotionWinner !== "none" && (this.isStandardArtwork || this.isDieCutSticker);
    },
    showBogoInfo() {
      return this.isDtfUsaStore && this.bogoEligibility.visible === true;
    },
    showProColorUpsell() {
      return this.isDtfUsaStore && !this.isGuest && this.promotionWinner === "none";
    },

    rootStyles() {
      return {
        "product-details-dtf": true,
        "product-details-dtf--jersey-numbers": this.isJerseyNumbers
      };
    },
    showFullWidthArtwork() {
      return !this.sticker;
    },
    showRedrawNote() {
      if (this.pdpType === PDP_TYPE_GANG_SHEETS) return false;
      return this.showRedrawUI;
    },
    showShippingProgress() {
      return this.firstArtwork && !this.firstArtwork.isSimulated && !this.isArtworkLoading;
    },
    showStoreCredit() {
      return (
        this.artworkUploaded &&
        !this.isSticker &&
        !this.isSpangle &&
        !this.isGuest &&
        Number(this.storeCreditInfo.orderSubtotal) > 0
      );
    },
    artworkEnhancementProcessing() {
      return (
        this.isStandardArtwork && !this.isArtworkLoading && !this.firstArtwork.enhancementCompleted
      );
    },
    showCancelButton() {
      const isEnhancing = this.artworkUploaded && this.artworkEnhancementProcessing;
      return !this.withError && !this.isSpangle && (this.isArtworkLoading || isEnhancing);
    },
    showFreeTransfersBadge() {
      if (!this.isBogoApplicable) {
        return false;
      }

      const isEnhancing = this.artworkUploaded && this.artworkEnhancementProcessing;
      const previewReady = !this.isArtworkLoading && !isEnhancing;

      if (!previewReady || !this.artworkUploaded) {
        return false;
      }

      if (this.cartHasBlanks !== true && this.cartHasBlanks !== "true") {
        return false;
      }

      if (this.isGuest) {
        return true;
      }

      if (this.isNewToTransfers === null || this.isNewToTransfers === undefined) {
        return false;
      }

      return this.isNewToTransfers === true;
    },
    freeTransfersBadgeMessage() {
      return `You have ${this.blankItemsCount} FREE Transfers!`;
    },
    hasPinningSessionArtworks() {
      return hasArtworksWithPinningSession(this.artworks);
    },
    shouldStartBackButtonDetection() {
      if (this.catalogNumber === "DTF001") {
        if (
          isDtfUserRetentionModalShown() ||
          (this.isPinBasedRedrawEnabled &&
            (this.showPinningModal || this.hasPinningSessionArtworks))
        ) {
          return false;
        }

        return this.hasUploadedAndEnhancedArtworks;
      }
      return false;
    },
    shouldStartInactivityMonitoring() {
      if (this.isStandardArtwork && this.catalogNumber === "DTF001") {
        if (
          isDtfUserRetentionModalShown() ||
          (this.isPinBasedRedrawEnabled &&
            (this.showPinningModal || this.hasPinningSessionArtworks))
        ) {
          return false;
        }

        const allArtworksUploaded = this.artworks.every(
          artwork =>
            artwork &&
            !artwork.isLoading &&
            !artwork.isSimulated &&
            !artwork.uploadError &&
            typeof parseInt(artwork.id, 10) === "number" &&
            !Number.isNaN(parseInt(artwork.id, 10))
        );

        if (!allArtworksUploaded) {
          return false;
        }

        return this.artworks.every(artwork => artwork.enhancementCompleted === true);
      }
      return false;
    },
    hasUploadedAndEnhancedArtworks() {
      return this.artworks.some(artwork => this.isUploadedAndEnhanced(artwork));
    },
    hasArtworksInProgress() {
      return this.artworks.some(artwork => this.isArtworkInProgress(artwork));
    }
  },
  watch: {
    lastConfirmedConfigSection(val) {
      if (val?.section) {
        this.activeConfigSection = val.section;
      }
    },
    isGenericErrorVisible(value) {
      if (this.showStepsUI) {
        return;
      }
      toggleGlobalDtfError(value);
    },
    artworkUploaded(newValue) {
      if (this.isStandardArtwork) {
        this.activeConfigSection = this.firstArtwork?.enhanceImage
          ? "jiffy-print-perfect"
          : "print-as-uploaded";

        if (newValue) {
          const newUrl = new URL(window.location);
          newUrl.searchParams.delete("artwork_urls");
          newUrl.searchParams.delete("file_content_type");
          window.history.replaceState({}, "", newUrl.toString());
        }
      }

      if (this.isSpangle && newValue) {
        const url = this.isSpangleReordering ? this.originalArtworkUrl : this.firstArtwork?.url;

        this.setOriginalArtworkUrl(url);
      }

      if (newValue) {
        this.checkCartForBlanks();
      }
    },
    shouldStartInactivityMonitoring(shouldStart) {
      if (shouldStart && this.transferPdpInactivityDetection) {
        this.triggerUserInactivityMonitoring();
      }
    },
    shouldStartBackButtonDetection(shouldStart) {
      if (shouldStart && this.transferPdpBackButtonDetection) {
        this.triggerUserBackButtonClickMonitoring();
      }
    },
    artworks: {
      handler(newArtworks, oldArtworks) {
        if (!oldArtworks || !this.shouldProcessArtworksChange()) return;

        const state = this.getArtworksState(newArtworks, oldArtworks);

        if (this.handleArtworksRemoved(state)) return;
        if (this.handleArtworksStarted(state)) return;
        if (this.handleArtworksCompleted(state)) return;

        this.handleNewArtworkUploaded(newArtworks, oldArtworks, state.hasArtworksInProgressNow);
      },
      deep: true,
      immediate: false
    },
    spangleMode(newValue) {
      if (newValue === SPANGLE_MODES.sizeEdited) {
        this.updateArtworkValues({ index: 0, ...this.lastSpangledDimensions });
      }
    },
    spangleVariations(newVariations) {
      const version = newVariations.find(({ algorithm }) => algorithm === this.selectedAlgorithm);
      this.selectVersion({ version });
    }
  },
  mounted() {
    initProductSavedCartsSelector(true);
    initDeliveryDateSimple();
    this.initDefaultValues();

    nextTick(() => {
      window.JiffyDeliverTo.reloadToggle();
    });

    document
      .querySelector(".js-dtf-product")
      ?.classList.remove("vue-product-details-dtf--mounting");

    this.checkFreeTransfersBadgeConditions();
    this.setupCartListeners();
  },
  beforeUnmount() {
    const store = useDtfStore();
    store.cleanupPolling();
    this.cleanupCartListeners();
  },
  methods: {
    ...mapActions(useDtfStore, [
      "addFilesToQueue",
      "getArtworksPricing",
      "updatePdpDates",
      "removeArtworkWithActivity",
      "initDefaultValues",
      "addSampleToCart",
      "updateEnableEnhancement",
      "updateArtworkValuesById",
      "setActiveConfigSection"
    ]),
    ...mapActions(useSpanglesStore, ["selectVersion"]),
    ...mapActions(useDtfStore, [
      "resetRemovalFile",
      "removeArtwork",
      "removeArtworkByIndex",
      "resetFilesError",
      "setReplaceArtwork",
      "hideDiscardModal",
      "dropMultiArtwork",
      "updateArtworkValues",
      "resetPuff",
      "setArtworkBackground",
      "handleCancelProcess",
      "setBlankItemsCount",
      "setTransfersInCartCount",
      "setIsNewToTransfers",
      "confirmPinningInvalidation",
      "cancelPinningInvalidation"
    ]),
    ...mapActions(useSpanglesStore, ["resetSpangles", "setOriginalArtworkUrl"]),
    showFileDialog(data) {
      // Source of the event will determine if we are replacing
      // an artwork
      this.setReplaceArtwork(Boolean(data?.replaceArtwork));
      this.$refs.file.click();
    },
    handleFileChange(event) {
      this.resetSpangles();
      this.resetPuff();
      const { files } = event.target;
      let artworkIndex = null;

      if (!files.length) return;
      if (files.length > 10) {
        this.showFilesError = true;
        return;
      }

      if (files.length <= 1 && this.replaceArtwork) {
        artworkIndex = 0;
      } else {
        this.artworks.forEach((artwork, index) => {
          if (isArtworkWithError(artwork)) {
            this.removeArtworkByIndex(index);
          }
        });
      }

      const uploadSource = event.target.getAttribute("data-upload-source");
      this.addFilesToQueue({ files, isDropEvent: false, artworkIndex, uploadSource });
      this.$refs.file.value = "";
      this.setReplaceArtwork(false);
    },
    changeArtworkBgColor(color) {
      this.setArtworkBackground(color);
    },
    handleDesignServiceClick() {
      if (this.shouldShowArtworkQualityConfigPanelAbAiMonetization) {
        this.activateDesignServiceForMonetizationV2();
      } else {
        this.setActiveConfigSection("jiffy-print-perfect");
      }
      this.focusDesignInstructionsTextarea();
    },
    activateDesignServiceForMonetizationV2() {
      // Click on the PrintPerfect tab if not already selected
      if (this.firstArtwork.enhancementTier !== "full_enhancements") {
        this.updateArtworkValuesById({
          id: this.firstArtwork.id,
          enhancementTier: "full_enhancements",
          pricing: this.cachedArtworkPrices?.full_enhancements || {},
          hasExpertEditAddon: true
        });
        this.updateEnableEnhancement({
          artworkId: this.firstArtwork.id,
          enableEnhancement: true,
          enhancementTier: "full_enhancements"
        });
      }
      // Check the expert edit addon checkbox if not already checked
      if (!this.firstArtwork.hasExpertEditAddon) {
        this.updateArtworkValuesById({
          id: this.firstArtwork.id,
          hasExpertEditAddon: true,
          enhancementNote: ""
        });
        this.getArtworksPricing();
      }
    },
    focusDesignInstructionsTextarea() {
      this.$nextTick(() => {
        const $customNotesTextarea = document.querySelector(".custom-notes__textarea");
        // RedrawNotes component uses dynamic IDs with index (defaults to 0)
        // Try the default ID first, then fall back to any redraw-note-* ID
        const $redrawTextarea =
          document.getElementById("redraw-note-0") ||
          document.querySelector('[id^="redraw-note-"]');
        const $textarea = $customNotesTextarea || $redrawTextarea;
        if ($textarea) {
          $textarea.focus();
          $textarea.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
    },
    onRemoveArtwork() {
      this.removeArtworkWithActivity(this.fileToRemove);
      this.getArtworksPricing();
      this.updatePdpDates();
      this.resetSpangles();
      this.resetPuff();
    },
    onDiscard() {
      this.hideDiscardModal();
      this.dropMultiArtwork();
      this.updatePdpDates();
      this.resetSpangles();
      this.resetPuff();
    },
    toggleComparisonModal() {
      this.showComparisonModal = !this.showComparisonModal;
    },
    setActiveConfigSection(section) {
      this.activeConfigSection = section;
    },
    async checkFreeTransfersBadgeConditions() {
      try {
        if (this.isGuest) {
          this.setIsNewToTransfers(true);
        } else {
          try {
            const response = await api.dtf.isNewToDtfForBogo();

            const isNew =
              typeof response === "string"
                ? response.trim().toLowerCase() === "true"
                : Boolean(response);

            this.setIsNewToTransfers(isNew);
          } catch (error) {
            console.warn("Failed to check if user is new to transfers:", error);
            this.setIsNewToTransfers(false);
          }
        }

        this.hasNoPrintTransfersHistory = this.isNewToTransfers;

        // Check if cart has at least one Blank item
        await this.checkCartForBlanks();
      } catch (error) {
        console.warn("Failed to check free transfers badge conditions:", error);
      }
    },
    async checkCartForBlanks() {
      try {
        const response = await api.vueCart.currentItems();
        if (Object.keys(response.items || {}).length === 0) {
          this.updateCartCounts(0, 0);
          return;
        }

        const cartResponse = await api.vueCart.getCartState({ cb: Date.now() });
        if (!cartResponse?.order_groups) {
          this.updateCartCounts(0, 0);
          return;
        }

        const groups = cartResponse.order_groups.groups || {};
        const { blanks, transfers } = this.countItemsInGroups(groups);

        this.updateCartCounts(blanks, transfers);
      } catch (error) {
        console.warn("Failed to check cart for blanks:", error);
        this.updateCartCounts(0, 0);
      }
    },
    updateCartCounts(blanksCount, transfersCount) {
      this.setBlankItemsCount(blanksCount);
      this.setTransfersInCartCount(transfersCount);
      this.cartHasBlanks = blanksCount > 0;
    },
    countItemsInGroups(groups) {
      return Object.values(groups).reduce(
        (totals, group) => {
          const lineItems = group?.line_items || group?.lineItems || {};
          const counts = this.countItemsInLineItems(lineItems);
          return {
            blanks: totals.blanks + counts.blanks,
            transfers: totals.transfers + counts.transfers
          };
        },
        { blanks: 0, transfers: 0 }
      );
    },
    countItemsInLineItems(lineItems) {
      return Object.values(lineItems).reduce(
        (totals, lineItem) => {
          if (!lineItem) return totals;
          const quantity = parseInt(lineItem.quantity, 10) || 0;
          const itemType = this.getLineItemType(lineItem);
          if (itemType === "blanks") {
            return { ...totals, blanks: totals.blanks + quantity };
          }
          if (itemType === "transfers") {
            // Only count DTF001 transfers for BOGO promotion calculation
            const productStyle = lineItem.productStyle || lineItem.product_style;
            const catalogNumber =
              lineItem.product?.catalog_number ||
              lineItem.catalog_number ||
              lineItem.product?.product_style ||
              productStyle;
            const isDtf001 =
              catalogNumber === DTF001_CATALOG_NUMBER || productStyle === DTF001_CATALOG_NUMBER;
            if (isDtf001) {
              return { ...totals, transfers: totals.transfers + quantity };
            }
          }
          return totals;
        },
        { blanks: 0, transfers: 0 }
      );
    },
    getLineItemType(lineItem) {
      const businessLine = String(lineItem.business_line || "").toLowerCase();
      const productBusinessLine = String(lineItem.product?.business_line || "").toLowerCase();
      if (businessLine === "blanks" || productBusinessLine === "blanks") return "blanks";
      if (businessLine === "transfers" || productBusinessLine === "transfers") return "transfers";
      return null;
    },
    setupCartListeners() {
      // Listen for cart updates to re-check for Blank items
      this.handleItemsAddedToCart = () => {
        this.checkCartForBlanks();
      };
      this.handleCartQuantityUpdated = () => {
        this.checkCartForBlanks();
      };

      EventBus.on(Events.ITEMS_ADDED_TO_CART, this.handleItemsAddedToCart);
      EventBus.on(Events.CART_QUANTITY_UPDATED, this.handleCartQuantityUpdated);
    },
    cleanupCartListeners() {
      if (this.handleItemsAddedToCart) {
        EventBus.removeEventListener(Events.ITEMS_ADDED_TO_CART, this.handleItemsAddedToCart);
      }
      if (this.handleCartQuantityUpdated) {
        EventBus.removeEventListener(Events.CART_QUANTITY_UPDATED, this.handleCartQuantityUpdated);
      }
    },
    isUploadedAndEnhanced(artwork) {
      return (
        artwork &&
        !artwork.isSimulated &&
        !artwork.isFile &&
        !artwork.uploadError &&
        !artwork.isLoading &&
        artwork.id &&
        typeof parseInt(artwork.id, 10) === "number" &&
        !Number.isNaN(parseInt(artwork.id, 10)) &&
        artwork.enhancementCompleted === true
      );
    },
    isArtworkInProgress(artwork) {
      // Check if artwork is loading or enhancement is in progress
      return (
        artwork &&
        !artwork.isSimulated &&
        !artwork.uploadError &&
        (artwork.isLoading ||
          (artwork.id &&
            typeof parseInt(artwork.id, 10) === "number" &&
            !Number.isNaN(parseInt(artwork.id, 10)) &&
            artwork.enhancementCompleted !== true))
      );
    },
    shouldProcessArtworksChange() {
      return (
        this.transferPdpInactivityDetection &&
        !isDtfUserRetentionModalShown() &&
        !this.showPinningModal &&
        !this.hasPinningSessionArtworks &&
        this.catalogNumber === "DTF001"
      );
    },
    getArtworksState(newArtworks, oldArtworks) {
      const hasArtworksInProgressNow = this.hasArtworksInProgress;
      const hadArtworksInProgressBefore = oldArtworks.some(artwork =>
        this.isArtworkInProgress(artwork)
      );
      const hasUploadedAndEnhancedNow = this.hasUploadedAndEnhancedArtworks;
      const hadUploadedAndEnhancedBefore = oldArtworks.some(artwork =>
        this.isUploadedAndEnhanced(artwork)
      );

      return {
        hasArtworksInProgressNow,
        hadArtworksInProgressBefore,
        hasUploadedAndEnhancedNow,
        hadUploadedAndEnhancedBefore
      };
    },
    handleArtworksRemoved(state) {
      if (state.hadUploadedAndEnhancedBefore && !state.hasUploadedAndEnhancedNow) {
        this.stopMonitoring();
        return true;
      }
      return false;
    },
    handleArtworksStarted(state) {
      if (!state.hadArtworksInProgressBefore && state.hasArtworksInProgressNow) {
        this.stopMonitoring();
        return true;
      }
      return false;
    },
    handleArtworksCompleted(state) {
      if (
        state.hadArtworksInProgressBefore &&
        !state.hasArtworksInProgressNow &&
        state.hasUploadedAndEnhancedNow
      ) {
        this.resetTimer();
        return true;
      }
      return false;
    },
    handleNewArtworkUploaded(newArtworks, oldArtworks, hasArtworksInProgressNow) {
      const newCount = newArtworks.filter(artwork => this.isUploadedAndEnhanced(artwork)).length;
      const oldCount = oldArtworks.filter(artwork => this.isUploadedAndEnhanced(artwork)).length;

      if (newCount > oldCount && !hasArtworksInProgressNow) {
        this.resetTimer();
      }
    }
  }
};
</script>

<style scoped lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";

.product-details-dtf {
  $this: &;

  position: relative;
  min-height: 440px;

  @include with-tablet {
    min-height: 490px;
  }

  @include with-desktop {
    min-height: 540px;
  }

  &__jersey-numbers,
  &__letters {
    margin-bottom: 12px;
  }

  &__free-transfers-badge {
    margin-bottom: 16px;
  }

  &__file-name {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    height: 36px;
    margin: -16px -18px 20px;
    background: $section-grey;
    border-bottom: 1px solid $gray-border;
    display: flex;
    align-items: center;
    font-size: 16px;
    font-weight: 700;
    padding: 12px;
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;

    &--with-error {
      background: $red-error-background-2;
    }

    &--full-width {
      @include only-mobile {
        margin: -16px -5px 20px;
      }
    }

    @include with-desktop {
      font-size: 20px;
      height: 48px;
      margin-top: -20px;
      padding-left: 20px;
    }
  }

  &__pro-color-upsell {
    margin-bottom: 16px;
    border-color: $app-gray-modern-300;
  }

  &__artwork {
    margin-bottom: 16px;
    padding-bottom: 0;

    &--with-border {
      border: 1px solid $gray-border;
      padding: 10px 15px 20px 15px;
      border-radius: 8px;
      box-shadow: 0 25px 30px 0 rgba($black, 0.05);
      background-color: $white;
    }

    &--with-border-error {
      border-color: $red-border;
    }
  }

  &__dimensions {
    &--near-quantity {
      border-bottom: none;
      margin-bottom: 4px;
    }
  }

  &__upload {
    border-top: 1px solid #e1e1e5;
    margin: 12px 0;
    padding-top: 10px;
  }

  &__header-icon {
    width: 20px;
    height: 20px;
    margin-right: 10px;
    color: $feedback-success-darker;

    &--with-error {
      background: $red-error-background-2;
      color: $red;
    }
  }

  &__header-text {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  &__pro-color {
    margin-top: 12px;

    @include with-tablet {
      margin-top: 24px;
    }
  }

  &--jersey-numbers {
    :deep(.artwork-quantity__row) {
      align-items: flex-start;
    }

    :deep(.artwork-quantity__value) {
      margin-right: 32px;

      @include with-tablet {
        margin-right: 40px;
      }
    }

    :deep(.artwork-quantity__total) {
      margin: 0;
    }

    :deep(.artwork-quantity__prices) {
      align-self: unset;
      margin: 0 32px 0 0;

      @include with-tablet {
        margin: 0 40px 0 0;
      }
    }

    :deep(.artwork-quantity__off) {
      margin-top: 40px;
    }
  }

  &__section {
    margin-top: 12px;

    @include with-tablet {
      margin-top: 24px;
    }
  }

  &__store-credit {
    margin-top: 10px;
  }

  &__cancel {
    font-size: 14px;
    font-weight: 700;
    color: $primary-light;
    background: none;
    border: none;
    display: flex;
    align-items: center;
    height: 15px;
    margin-bottom: 5px;
  }

  &__cancel-icon {
    width: 14px;
    height: 14px;
    margin-right: 3px;
  }

  &__or {
    position: relative;
    display: inline-block;
    padding: 0 10px;
    text-align: center;
    width: 100%;
    font-weight: 700;

    &::before,
    &::after {
      content: "";
      position: absolute;
      top: 50%;
      height: 1px;
      background-color: $gray-border;
      width: 45%;

      @include with-desktop {
        width: 47%;
      }
    }

    &::before {
      right: 0;
    }

    &::after {
      left: 0;
    }
  }
}

.product-details-dtf__promotion-banner :deep([data-promotion-winner]) {
  display: none;
  margin-bottom: 16px;
}

.product-details-dtf__promotion-banner--winner-sticker :deep([data-promotion-winner="sticker"]) {
  display: block;
}
.product-details-dtf__promotion-banner--winner-print_wholesale
  :deep([data-promotion-winner="print_wholesale"]) {
  display: block;
}
.product-details-dtf__promotion-banner--winner-print_coupon
  :deep([data-promotion-winner="print_coupon"]) {
  display: block;
}
</style>

<style lang="scss">
@import "frontend/init/jiffy/mixin.scss";

.product-details-dtf__dates-container {
  padding: 20px 0 0;
  margin-bottom: 25px;
  padding: 0;

  .product-details-quantity-form-row {
    &__shipping-progress-bar-wrapper {
      padding: 0 20px 20px;
      border-bottom: 1px solid $gray-border;
    }
  }
}
</style>
